import React, { createElement } from 'react';
import { CheckCircleIcon, CalendarIcon, ArrowUpIcon } from 'lucide-react';
import { motion } from 'framer-motion';
interface ConfirmationScreenProps {
  date: Date;
  time: string;
  primaryInterest: string;
  email: string;
  leadData: any;
  onReset: () => void;
}
export function ConfirmationScreen({
  date,
  time,
  primaryInterest,
  email,
  leadData,
  onReset
}: ConfirmationScreenProps) {
  const formattedDate = date.toLocaleDateString('en-AU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
  const [startTime, endTime] = calculateTimeRange(time);
  const handleAddToCalendar = () => {
    const icsContent = generateICS({
      date,
      startTime,
      endTime,
      leadData,
      primaryInterest
    });
    const blob = new Blob([icsContent], {
      type: 'text/calendar;charset=utf-8'
    });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'scale-finance-consultation.ics';
    link.click();
  };
  const handleReturnHome = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    setTimeout(onReset, 500);
  };
  return <motion.div initial={{
    opacity: 0,
    scale: 0.95
  }} animate={{
    opacity: 1,
    scale: 1
  }} transition={{
    duration: 0.4
  }} className="text-center space-y-6" data-id="element-84">
      <motion.div initial={{
      scale: 0
    }} animate={{
      scale: 1
    }} transition={{
      delay: 0.2,
      type: 'spring',
      stiffness: 200
    }} data-id="element-85">
        <CheckCircleIcon className="w-20 h-20 text-green-400 mx-auto mb-4" data-id="element-86" />
      </motion.div>

      <div data-id="element-87">
        <h3 className="text-3xl font-bold text-slate-900 dark:text-white mb-2" data-id="element-88">
          You're All Set!
        </h3>
        <p className="text-lg text-slate-500 dark:text-slate-400" data-id="element-89">
          Your consultation has been booked.
        </p>
      </div>

      <div className="bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-2xl p-6 backdrop-blur-sm max-w-md mx-auto" data-id="element-90">
        <div className="space-y-4 text-left" data-id="element-91">
          <div className="flex items-start gap-3" data-id="element-92">
            <CalendarIcon className="w-5 h-5 text-primary-500 dark:text-primary-400 mt-0.5 flex-shrink-0" data-id="element-93" />
            <div data-id="element-94">
              <p className="text-sm text-slate-500 dark:text-slate-400" data-id="element-95">Date</p>
              <p className="text-slate-900 dark:text-white font-medium" data-id="element-96">
                {formattedDate}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3" data-id="element-97">
            <CalendarIcon className="w-5 h-5 text-accent-500 dark:text-accent-400 mt-0.5 flex-shrink-0" data-id="element-98" />
            <div data-id="element-99">
              <p className="text-sm text-slate-500 dark:text-slate-400" data-id="element-100">Time</p>
              <p className="text-slate-900 dark:text-white font-medium" data-id="element-101">
                {startTime} - {endTime} AWST
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3" data-id="element-102">
            <CalendarIcon className="w-5 h-5 text-teal-400 mt-0.5 flex-shrink-0" data-id="element-103" />
            <div data-id="element-104">
              <p className="text-sm text-slate-500 dark:text-slate-400" data-id="element-105">
                Duration
              </p>
              <p className="text-slate-900 dark:text-white font-medium" data-id="element-106">
                30 minutes
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3" data-id="element-107">
            <CalendarIcon className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" data-id="element-108" />
            <div data-id="element-109">
              <p className="text-sm text-slate-500 dark:text-slate-400" data-id="element-110">Type</p>
              <p className="text-slate-900 dark:text-white font-medium" data-id="element-111">
                {primaryInterest}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-3 max-w-md mx-auto" data-id="element-112">
        <p className="text-slate-500 dark:text-slate-400 text-sm" data-id="element-113">
          A calendar invite and confirmation email have been sent to{' '}
          <span className="text-primary-600 dark:text-primary-400 font-medium" data-id="element-114">
            {email}
          </span>
          .
        </p>
        <p className="text-slate-500 dark:text-slate-400 text-sm" data-id="element-115">
          One of our strategists will be in touch before your session.
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 pt-4 max-w-md mx-auto" data-id="element-116">
        <button onClick={handleAddToCalendar} className="flex-1 py-3 px-6 rounded-xl bg-slate-100 dark:bg-white/5 hover:bg-slate-200 dark:hover:bg-white/10 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white font-medium transition-all flex items-center justify-center gap-2" data-id="element-117">
          <CalendarIcon className="w-5 h-5" data-id="element-118" />
          Add to Calendar
        </button>
        <button onClick={handleReturnHome} className="flex-1 py-3 px-6 rounded-xl bg-gradient-to-r from-accent-500 to-primary-500 text-white font-semibold hover:shadow-[0_0_20px_rgba(20,184,166,0.4)] transition-all flex items-center justify-center gap-2" data-id="element-119">
          <ArrowUpIcon className="w-5 h-5" data-id="element-120" />
          Return to Home
        </button>
      </div>
    </motion.div>;
}
// Helper function to calculate end time
function calculateTimeRange(startTime: string): [string, string] {
  const [time, period] = startTime.split(' ');
  const [hours, minutes] = time.split(':').map(Number);
  let endHours = hours;
  let endMinutes = minutes + 30;
  if (endMinutes >= 60) {
    endMinutes -= 60;
    endHours += 1;
  }
  let endPeriod = period;
  if (endHours === 12 && minutes < 30) {
    endPeriod = period === 'AM' ? 'PM' : 'AM';
  } else if (endHours > 12) {
    endHours -= 12;
    endPeriod = 'PM';
  }
  const endTime = `${endHours}:${endMinutes.toString().padStart(2, '0')} ${endPeriod}`;
  return [startTime, endTime];
}
// Helper function to generate ICS file
function generateICS(data: {
  date: Date;
  startTime: string;
  endTime: string;
  leadData: any;
  primaryInterest: string;
}): string {
  const {
    date,
    startTime,
    endTime,
    leadData,
    primaryInterest
  } = data;
  // Convert time to 24-hour format for ICS
  const start24 = convertTo24Hour(startTime);
  const end24 = convertTo24Hour(endTime);
  const startDateTime = new Date(date);
  const [startHours, startMinutes] = start24.split(':').map(Number);
  startDateTime.setHours(startHours, startMinutes, 0);
  const endDateTime = new Date(date);
  const [endHours, endMinutes] = end24.split(':').map(Number);
  endDateTime.setHours(endHours, endMinutes, 0);
  const formatICSDate = (d: Date) => {
    return d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  };
  const description = `Consultation with ${leadData.full_name}\\n\\nContact Details:\\nEmail: ${leadData.email}\\nPhone: ${leadData.phone}\\n\\nPrimary Interest: ${primaryInterest}\\n\\nMessage:\\n${leadData.message}`;
  return `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Scale Finance Strategies//Consultation Booking//EN
BEGIN:VEVENT
UID:${Date.now()}@scalefinancestrategies.com.au
DTSTAMP:${formatICSDate(new Date())}
DTSTART:${formatICSDate(startDateTime)}
DTEND:${formatICSDate(endDateTime)}
SUMMARY:Strategy Consultation - ${leadData.full_name}
DESCRIPTION:${description}
LOCATION:Online/Phone
STATUS:CONFIRMED
SEQUENCE:0
END:VEVENT
END:VCALENDAR`;
}
function convertTo24Hour(time: string): string {
  const [timePart, period] = time.split(' ');
  let [hours, minutes] = timePart.split(':').map(Number);
  if (period === 'PM' && hours !== 12) {
    hours += 12;
  } else if (period === 'AM' && hours === 12) {
    hours = 0;
  }
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
}